import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaAlignLeft, FaAlignCenter, FaAlignRight } from 'react-icons/fa';
import { FiMinus, FiPlus, FiX } from 'react-icons/fi';
import { HANDLES, HANDLE_SIZE, NUDGE_STEP, PRESETS, placeToolbar } from './imageSizing';

const boxShape = PropTypes.shape({
  top: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
});

const ALIGNMENTS = [
  { key: 'left', Icon: FaAlignLeft, title: 'Float left' },
  { key: 'center', Icon: FaAlignCenter, title: 'Center' },
  { key: 'right', Icon: FaAlignRight, title: 'Float right' },
];

/**
 * Mousedown on a control would otherwise collapse the caret and, with it, the
 * contentEditable selection. Applied per button — never to the whole toolbar,
 * because that would also make the percent field impossible to focus.
 */
const keepCaret = { onMouseDown: (event) => event.preventDefault() };

const BUTTON_CLS = 'tw-h-6 tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-border-0 '
  + 'tw-bg-transparent tw-cursor-pointer tw-text-slate-600 hover:tw-bg-slate-100 hover:tw-text-primary-700';

const Divider = () => <div className="tw-w-px tw-h-4 tw-bg-slate-200 tw-mx-0.5" />;

/**
 * Selection outline, resize handles and the floating image toolbar.
 *
 * Everything lives inside a layer clipped to the editor's scroll viewport, so an
 * image scrolled out of view can never leave controls floating over the format
 * toolbar. All geometry is inline `style` — Tailwind's JIT only sees literal
 * class strings, so computed offsets cannot be classes.
 */
const ImageResizeOverlay = ({
  box, viewport,
  widthPercent = null, align = null, dragPercent = null, isDragging = false,
  onAlign, onWidthChange, onNudge, onReset, onDelete, getHandleProps,
}) => {
  const toolbarRef = useRef(null);
  const [toolbarSize, setToolbarSize] = useState(null);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);

  // The toolbar's width changes with its own labels, so measure it rather than
  // assume — `placeToolbar` needs a real width to clamp against the viewport.
  useLayoutEffect(() => {
    const node = toolbarRef.current;
    if (!node) return undefined;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setToolbarSize((prev) => (
        prev && prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height }
      ));
    };
    measure();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (!editing) setDraft(widthPercent == null ? '' : String(widthPercent));
  }, [widthPercent, editing]);

  const commitDraft = () => {
    setEditing(false);
    const next = parseInt(draft, 10);
    onWidthChange(Number.isFinite(next) ? next : null);
  };

  /**
   * Every button here suppresses mousedown to keep the caret, which also means
   * the percent field never loses focus when one is clicked — so its draft has
   * to be released explicitly, or it would sit frozen on a stale number.
   */
  const releasingDraft = (fn) => (...args) => {
    setEditing(false);
    fn(...args);
  };

  const placed = placeToolbar({ box, viewport, toolbar: toolbarSize });

  // Inner coordinates are relative to the clip layer, which sits at the viewport.
  const localTop = box.top - viewport.top;
  const localLeft = box.left - viewport.left;

  return (
    <div
      className="tw-absolute tw-overflow-hidden tw-pointer-events-none tw-z-20"
      style={{ top: viewport.top, left: viewport.left, width: viewport.width, height: viewport.height }}
    >
      <div
        className="tw-absolute"
        style={{ top: localTop, left: localLeft, width: box.width, height: box.height }}
      >
        <div className="tw-absolute tw-inset-0 tw-border-2 tw-border-primary-500 tw-rounded-[6px]" />

        {HANDLES.map(({ key, cursor, position }) => (
          <button
            key={key}
            type="button"
            tabIndex={-1}
            aria-label={`Resize image (${key})`}
            {...getHandleProps(key)}
            className="tw-absolute tw-pointer-events-auto tw-p-0 tw-bg-white tw-border-2 tw-border-primary-500 tw-rounded-sm"
            // touchAction:none or a touch drag scrolls the page instead of resizing.
            style={{ ...position, width: HANDLE_SIZE, height: HANDLE_SIZE, cursor, touchAction: 'none' }}
          />
        ))}

        {isDragging && (
          <div
            className="tw-absolute tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-slate-900/80 tw-text-white tw-text-[10px] tw-font-semibold tw-whitespace-nowrap"
            style={{ left: '50%', bottom: 6, transform: 'translateX(-50%)' }}
          >
            {dragPercent}%
            <span className="tw-font-normal tw-opacity-70"> · {Math.round(box.width)}px</span>
          </div>
        )}
      </div>

      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label="Image options"
        className="tw-absolute tw-pointer-events-auto tw-flex tw-items-center tw-gap-0.5 tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-lg tw-px-1 tw-py-0.5"
        style={placed
          ? { top: placed.top - viewport.top, left: placed.left - viewport.left }
          : { top: 0, left: 0, visibility: 'hidden' }}
      >
        {ALIGNMENTS.map(({ key, Icon, title }) => (
          <button
            key={key}
            type="button"
            title={title}
            {...keepCaret}
            onClick={releasingDraft(() => onAlign(key))}
            className={`${BUTTON_CLS} tw-w-6 ${align === key ? 'tw-bg-primary-50 tw-text-primary-700' : ''}`}
          >
            <Icon size={11} />
          </button>
        ))}

        <Divider />

        <button
          type="button"
          title={`Narrower (−${NUDGE_STEP}%)`}
          {...keepCaret}
          onClick={releasingDraft(() => onNudge(-NUDGE_STEP, true))}
          className={`${BUTTON_CLS} tw-w-6`}
        >
          <FiMinus size={12} />
        </button>
        <div className="tw-flex tw-items-center tw-gap-0.5">
          <input
            type="text"
            inputMode="numeric"
            value={draft}
            placeholder="auto"
            aria-label="Image width in percent"
            onFocus={() => setEditing(true)}
            onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); commitDraft(); }
              if (event.key === 'Escape') { event.preventDefault(); setEditing(false); }
            }}
            className="tw-w-9 tw-h-5 tw-px-1 tw-text-[11px] tw-text-center tw-text-slate-700 tw-bg-white tw-border tw-border-slate-200 tw-rounded-sm tw-outline-none focus:tw-border-primary-500"
          />
          <span className="tw-text-[10px] tw-text-slate-400">%</span>
        </div>
        <button
          type="button"
          title={`Wider (+${NUDGE_STEP}%)`}
          {...keepCaret}
          onClick={releasingDraft(() => onNudge(NUDGE_STEP, true))}
          className={`${BUTTON_CLS} tw-w-6`}
        >
          <FiPlus size={12} />
        </button>

        <Divider />

        {PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            title={`Width ${preset}%`}
            {...keepCaret}
            onClick={releasingDraft(() => onWidthChange(preset))}
            className={`${BUTTON_CLS} tw-px-1.5 tw-text-[10px] tw-font-semibold ${widthPercent === preset ? 'tw-bg-primary-50 tw-text-primary-700' : ''}`}
          >
            {preset}%
          </button>
        ))}
        <button
          type="button"
          title="Natural size"
          {...keepCaret}
          onClick={releasingDraft(onReset)}
          className={`${BUTTON_CLS} tw-px-1.5 tw-text-[10px] tw-font-semibold ${widthPercent == null ? 'tw-bg-primary-50 tw-text-primary-700' : ''}`}
        >
          Auto
        </button>

        <Divider />

        <button
          type="button"
          title="Remove image (Delete)"
          {...keepCaret}
          onClick={onDelete}
          className="tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-rose-500 hover:tw-bg-rose-50"
        >
          <FiX size={13} />
        </button>
      </div>
    </div>
  );
};

ImageResizeOverlay.propTypes = {
  box: boxShape.isRequired,
  viewport: boxShape.isRequired,
  widthPercent: PropTypes.number,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  dragPercent: PropTypes.number,
  isDragging: PropTypes.bool,
  onAlign: PropTypes.func.isRequired,
  onWidthChange: PropTypes.func.isRequired,
  onNudge: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  getHandleProps: PropTypes.func.isRequired,
};

export default ImageResizeOverlay;
